import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useVirtualizer } from '@tanstack/react-virtual';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from '@/components/ui/select';
import { RefreshCw, Loader2, Share2 } from 'lucide-react';
import { automationService, type StrategyTrade, type RuleStatistics, type ScenarioSummary } from '@/services/strategyService';
import { ShareScenarioDialog } from './ShareScenarioDialog';
import { API_BASE } from '@/env';
import { apiFetch } from '@/auth/apiClient';

// ── Helpers ──────────────────────────────────────────────────────

function fmtNY(isoUtc: string | null | undefined, withSeconds = false): string {
  if (!isoUtc) return '-';
  const d = new Date(isoUtc);
  const opts: Intl.DateTimeFormatOptions = {
    timeZone: 'America/New_York',
    month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit',
    hour12: false,
    ...(withSeconds ? { second: '2-digit' } : {}),
  };
  return new Intl.DateTimeFormat('en-CA', opts).format(d).replace(',', '');
}

function fmtHold(secs: number): string {
  if (secs < 60) return `${secs}s`;
  if (secs < 3600) return `${Math.round(secs / 60)}m`;
  return `${(secs / 3600).toFixed(1)}h`;
}

// Richer hold-time formatter for the stats header — "Xh Ym" / "Xm Ys".
function fmtHoldRich(secs: number): string {
  const s = Math.max(0, Math.round(secs));
  if (s < 60) return `${s}s`;
  if (s < 3600) {
    const m = Math.floor(s / 60);
    const r = s % 60;
    return r > 0 ? `${m}m ${r}s` : `${m}m`;
  }
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  return m > 0 ? `${h}h ${m}m` : `${h}h`;
}

// Display-friendly exit label: TakeProfit on positive PnL, StopLoss on negative.
// Underlying exitReason still shown in the detail header for accuracy.
function exitLabel(t: StrategyTrade): string {
  if (t.exitReason == null) return '-';
  const pnl = t.realizedPnL ?? 0;
  if (pnl > 0) return 'TakeProfit';
  if (pnl < 0) return 'StopLoss';
  return t.exitReason;
}

// Parse "v7 Volume Surge + Percent Based — Feb 02 to May 03" (or
// "v7b-SHORT ORB + Momentum Runner …") into { run, entry, mgmt } so the picker
// can build a Run → Entry → Mgmt → Window drill-down.
function parseScenarioName(name: string): { run: string; entry: string; mgmt: string } {
  if (!name) return { run: '(other)', entry: '(other)', mgmt: '(other)' };
  const head = name.split(/\s+[—-]\s+/)[0]?.trim() ?? name;
  const plusIdx = head.indexOf(' + ');
  if (plusIdx < 0) return { run: '(other)', entry: head, mgmt: '(other)' };
  const before = head.slice(0, plusIdx);
  const mgmt = head.slice(plusIdx + 3).trim();
  // First whitespace-delimited token is the run prefix ("v7", "v7b-SHORT", etc.)
  const m = before.match(/^(v\d+\S*)\s+(.+)$/i);
  if (m) return { run: m[1], entry: m[2].trim(), mgmt };
  return { run: '(other)', entry: before.trim(), mgmt };
}

// ── Types ──────────────────────────────────────────────────────

type SortKey =
  | 'symbol' | 'direction' | 'entryTime' | 'entryPrice' | 'exitPrice'
  | 'realizedPnL' | 'realizedRMultiple' | 'exitReason' | 'holdingSeconds';

interface SortState { col: SortKey; dir: 'asc' | 'desc'; }

interface MassiveBar {
  timestamp: string;
  symbol: string;
  open: number;
  high: number;
  low: number;
  close: number;
  volume: number;
}

// ── Hash query helpers — keep ?scenario=… &trade=… in window.location.hash so
// shareable URLs land on a specific scenario (and optionally trade index).

function readHashQuery(): URLSearchParams {
  const hash = window.location.hash;
  const qIdx = hash.indexOf('?');
  return new URLSearchParams(qIdx >= 0 ? hash.slice(qIdx + 1) : '');
}

function writeHashQuery(params: Record<string, string | null>) {
  const current = readHashQuery();
  for (const [k, v] of Object.entries(params)) {
    if (v == null || v === '') current.delete(k);
    else current.set(k, v);
  }
  const hash = window.location.hash;
  const tabPart = hash.split('?')[0] || '#/dashboard/trade-viewer';
  const qs = current.toString();
  const newHash = qs ? `${tabPart}?${qs}` : tabPart;
  window.history.replaceState(null, '', window.location.pathname + window.location.search + newHash);
}

// ── Component ──────────────────────────────────────────────────

export const TradeViewerPage: React.FC = () => {
  // Each level loads on demand from the backend — no full-list fetch.
  const [runs, setRuns] = useState<string[]>([]);
  const [entries, setEntries] = useState<string[]>([]);
  const [mgmts, setMgmts] = useState<string[]>([]);
  const [windows, setWindows] = useState<ScenarioSummary[]>([]);
  const [runsLoading, setRunsLoading] = useState(false);

  const [pickedRun, setPickedRun] = useState<string>('');
  const [pickedEntry, setPickedEntry] = useState<string>('');
  const [pickedMgmt, setPickedMgmt] = useState<string>('');

  const [selectedScenarioId, setSelectedScenarioId] = useState<string>('');
  const [scenarioName, setScenarioName] = useState<string>('');
  const [trades, setTrades] = useState<StrategyTrade[]>([]);
  // Backend-computed RuleStatistics (full metric set: PF, expectancy, avg R, max DD, …).
  const [scenarioStats, setScenarioStats] = useState<RuleStatistics | null>(null);
  const [sort, setSort] = useState<SortState>({ col: 'entryTime', dir: 'asc' });
  const [selectedTradeIdx, setSelectedTradeIdx] = useState<number>(-1);
  const [shareOpen, setShareOpen] = useState(false);
  // Shared read-only mode: the page was opened via ?key=<token>. The viewer
  // may not own the scenario, so the owner-scoped drill-down pickers are
  // hidden and only the resolved scenario is shown.
  const [sharedMode, setSharedMode] = useState(false);
  const [shareLoadError, setShareLoadError] = useState(false);

  // On mount: ?key=<token> → shared read-only mode; otherwise load the run
  // list and auto-open ?scenario=<id> for the owner.
  useEffect(() => {
    (async () => {
      const params = readHashQuery();
      const key = params.get('key');
      const tIdx = params.get('trade');
      // Shared link — resolve the token via the anonymous-safe by-share
      // endpoint. Skip the owner-scoped run list entirely.
      if (key) {
        setSharedMode(true);
        await loadSharedScenario(key);
        if (tIdx) {
          const idx = parseInt(tIdx, 10);
          if (!isNaN(idx)) setSelectedTradeIdx(idx);
        }
        return;
      }
      await refreshRuns();
      const sid = params.get('scenario');
      if (sid) {
        await loadScenario(sid);
        if (tIdx) {
          const idx = parseInt(tIdx, 10);
          if (!isNaN(idx)) setSelectedTradeIdx(idx);
        }
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Cascade: run change → load entries
  useEffect(() => {
    if (!pickedRun) { setEntries([]); return; }
    automationService.getScenarioEntries(pickedRun)
      .then(setEntries)
      .catch(err => console.error('failed to load entries', err));
  }, [pickedRun]);

  // Cascade: entry change → load mgmts
  useEffect(() => {
    if (!pickedRun || !pickedEntry) { setMgmts([]); return; }
    automationService.getScenarioMgmts(pickedRun, pickedEntry)
      .then(setMgmts)
      .catch(err => console.error('failed to load mgmts', err));
  }, [pickedRun, pickedEntry]);

  // Cascade: mgmt change → load windows. Auto-pick the latest only when the
  // currently-selected scenario is NOT one of the windows for this combo —
  // otherwise the deep-linked scenario would get clobbered by the latest
  // window (most often the matrix run, dated today) on arrival.
  useEffect(() => {
    if (!pickedRun || !pickedEntry || !pickedMgmt) { setWindows([]); return; }
    automationService.getScenarioWindows(pickedRun, pickedEntry, pickedMgmt)
      .then(ws => {
        setWindows(ws);
        if (ws.length === 0) return;
        const hasCurrent = ws.some(w => w.id === selectedScenarioId);
        if (!hasCurrent) loadScenario(ws[ws.length - 1].id);
      })
      .catch(err => console.error('failed to load windows', err));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pickedRun, pickedEntry, pickedMgmt]);

  // Sync trade index → URL (when user clicks a different trade)
  useEffect(() => {
    if (selectedScenarioId) {
      writeHashQuery({ trade: selectedTradeIdx >= 0 ? String(selectedTradeIdx) : null });
    }
  }, [selectedTradeIdx, selectedScenarioId]);

  const refreshRuns = async () => {
    setRunsLoading(true);
    try {
      const list = await automationService.getScenarioRuns();
      setRuns(list);
    } catch (err) {
      console.error('failed to load runs', err);
    } finally {
      setRunsLoading(false);
    }
  };

  const loadScenario = async (scenarioId: string) => {
    setSelectedScenarioId(scenarioId);
    writeHashQuery({ scenario: scenarioId, trade: null });
    if (!scenarioId) { setTrades([]); setScenarioName(''); setScenarioStats(null); return; }
    try {
      const s = await automationService.getScenario(scenarioId);
      setTrades(s.trades || []);
      setScenarioName(s.name || s.id.slice(0, 8));
      setScenarioStats(s.statistics ?? null);
      setSelectedTradeIdx(-1);
      // Sync picker state to whatever scenario we just loaded (handles deep-link
      // arrivals consistently)
      const parsed = parseScenarioName(s.name);
      setPickedRun(parsed.run);
      setPickedEntry(parsed.entry);
      setPickedMgmt(parsed.mgmt);
    } catch (err) {
      console.error('failed to load scenario', err);
    }
  };

  // Resolve a ?key=<token> share link to its scenario. The viewer may not own
  // the scenario, so this uses the anonymous-safe by-share endpoint and does
  // NOT sync the owner-scoped picker state.
  const loadSharedScenario = async (token: string) => {
    try {
      const s = await automationService.getScenarioByShare(token);
      setSelectedScenarioId(s.id);
      setTrades(s.trades || []);
      setScenarioName(s.name || s.id.slice(0, 8));
      setScenarioStats(s.statistics ?? null);
      setSelectedTradeIdx(-1);
    } catch (err) {
      console.error('failed to load shared scenario', err);
      setShareLoadError(true);
    }
  };

  // Sorted trades
  const sortedTrades = useMemo(() => {
    const arr = [...trades];
    arr.sort((a, b) => {
      const av = (a as any)[sort.col];
      const bv = (b as any)[sort.col];
      if (av == null) return 1;
      if (bv == null) return -1;
      const cmp = av < bv ? -1 : av > bv ? 1 : 0;
      return sort.dir === 'asc' ? cmp : -cmp;
    });
    return arr;
  }, [trades, sort]);

  const selectedTrade = selectedTradeIdx >= 0 ? trades[selectedTradeIdx] : null;

  return (
    <div className="space-y-3 p-5">
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex flex-col gap-1 sm:flex-row sm:items-center sm:justify-between">
            <span>Trade Viewer</span>
            <span className="text-xs text-muted-foreground font-normal">
              Inspect individual backtest trades against the bar data the engine consumed
            </span>
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-0">
          <div className="flex flex-wrap items-center gap-2">
            {sharedMode && (
              <span className="text-[11px] px-2 py-1 rounded bg-blue-500/15 text-blue-400 font-medium">
                Shared scenario · read-only
              </span>
            )}
            {!sharedMode && (<>
            {/* Run */}
            <div className="flex flex-col">
              <span className="text-[10px] text-muted-foreground mb-0.5">Run</span>
              <Select
                value={pickedRun || undefined}
                onValueChange={(v) => { setPickedRun(v); setPickedEntry(''); setPickedMgmt(''); }}
              >
                <SelectTrigger className="h-9 w-[120px]"><SelectValue placeholder="—" /></SelectTrigger>
                <SelectContent className="bg-popover/100 backdrop-blur-md z-[100]">
                  {runs.map(r => (
                    <SelectItem key={r} value={r}>{r}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Entry */}
            <div className="flex flex-col">
              <span className="text-[10px] text-muted-foreground mb-0.5">Entry strategy</span>
              <Select
                value={pickedEntry || undefined}
                onValueChange={(v) => { setPickedEntry(v); setPickedMgmt(''); }}
                disabled={!pickedRun}
              >
                <SelectTrigger className="h-9 w-[200px]"><SelectValue placeholder={pickedRun ? '— entry —' : 'pick a run first'} /></SelectTrigger>
                <SelectContent className="bg-popover/100 backdrop-blur-md z-[100] max-h-[400px]">
                  {entries.map(e => (
                    <SelectItem key={e} value={e}>{e}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Mgmt */}
            <div className="flex flex-col">
              <span className="text-[10px] text-muted-foreground mb-0.5">Mgmt style</span>
              <Select
                value={pickedMgmt || undefined}
                onValueChange={setPickedMgmt}
                disabled={!pickedEntry}
              >
                <SelectTrigger className="h-9 w-[180px]"><SelectValue placeholder={pickedEntry ? '— mgmt —' : 'pick entry'} /></SelectTrigger>
                <SelectContent className="bg-popover/100 backdrop-blur-md z-[100] max-h-[400px]">
                  {mgmts.map(m => (
                    <SelectItem key={m} value={m}>{m}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Window — only matters if there's >1 (rolling-window runs) */}
            {pickedMgmt && windows.length > 0 && (
              <div className="flex flex-col">
                <span className="text-[10px] text-muted-foreground mb-0.5">
                  Window {windows.length > 1 ? `(${windows.length})` : ''}
                </span>
                <Select
                  value={selectedScenarioId || undefined}
                  onValueChange={loadScenario}
                >
                  <SelectTrigger className="h-9 w-[230px]"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-popover/100 backdrop-blur-md z-[100] max-h-[400px]">
                    {windows.map(s => (
                      <SelectItem key={s.id} value={s.id}>
                        {s.backtest?.startDate ?? ''} → {s.backtest?.endDate ?? ''} · {s.tradeCount ?? 0} trades
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            <Button
              variant="outline"
              size="sm"
              onClick={refreshRuns}
              disabled={runsLoading}
              title="Refresh runs list"
            >
              {runsLoading
                ? <Loader2 className="h-4 w-4 animate-spin" />
                : <RefreshCw className="h-4 w-4" />}
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShareOpen(true)}
              disabled={!selectedScenarioId}
              title={selectedScenarioId ? 'Create an expiring share link for this scenario' : 'Pick a scenario first'}
            >
              <Share2 className="h-4 w-4 mr-2" /> Share
            </Button>
            </>)}
            {scenarioStats && (() => {
              const ss = scenarioStats;
              const totalCls = ss.totalPnL >= 0 ? 'text-green-500' : 'text-red-500';
              return (
                <div className="flex flex-col items-end gap-0.5 ml-auto text-xs">
                  <span className="text-muted-foreground">
                    Scenario: {scenarioName} · {ss.winners}/{ss.totalTrades} wins ({ss.winRate.toFixed(1)}%) · total{' '}
                    <span className={`tabular-nums ${totalCls}`}>${ss.totalPnL.toFixed(0)}</span>
                  </span>
                  <span className="text-muted-foreground/80 text-[11px] tabular-nums">
                    <span className="text-muted-foreground">PF</span>{' '}
                    {isFinite(ss.profitFactor) ? ss.profitFactor.toFixed(2) : '∞'}
                    <span className="mx-1.5 opacity-50">|</span>
                    <span className="text-muted-foreground">Avg R</span>{' '}
                    {ss.avgRMultiple.toFixed(2)}
                    <span className="mx-1.5 opacity-50">|</span>
                    <span className="text-muted-foreground">Exp</span>{' '}
                    <span className={ss.expectancy >= 0 ? 'text-green-500' : 'text-red-500'}>
                      ${ss.expectancy.toFixed(2)}
                    </span>
                    <span className="mx-1.5 opacity-50">|</span>
                    <span className="text-muted-foreground">Avg W</span>{' '}
                    <span className="text-green-500">${ss.avgWin.toFixed(0)}</span>
                    <span className="mx-1.5 opacity-50">|</span>
                    <span className="text-muted-foreground">Avg L</span>{' '}
                    <span className="text-red-500">${ss.avgLoss.toFixed(0)}</span>
                    <span className="mx-1.5 opacity-50">|</span>
                    <span className="text-muted-foreground">Max DD</span>{' '}
                    <span className="text-red-500">
                      ${ss.maxDrawdown.toFixed(0)} ({ss.maxDrawdownPercent.toFixed(1)}%)
                    </span>
                    <span className="mx-1.5 opacity-50">|</span>
                    <span className="text-muted-foreground">Avg hold</span>{' '}
                    {fmtHoldRich(ss.avgHoldingSeconds)}
                  </span>
                </div>
              );
            })()}
          </div>
        </CardContent>
      </Card>

      {shareLoadError && (
        <Card className="p-3 text-sm text-red-500">
          This share link is invalid or has expired.
        </Card>
      )}

      {!sharedMode && selectedScenarioId && (
        <ShareScenarioDialog
          runId={selectedScenarioId}
          scenarioName={scenarioName}
          open={shareOpen}
          onOpenChange={setShareOpen}
        />
      )}

      <div className="grid grid-cols-1 lg:grid-cols-[520px_1fr] gap-3 lg:h-[calc(100vh_-_220px)]">
        <TradeTable
          trades={sortedTrades}
          allTrades={trades}
          sort={sort}
          setSort={setSort}
          selectedIdx={selectedTradeIdx}
          onSelect={setSelectedTradeIdx}
        />
        <div className="flex flex-col gap-2 min-h-0">
          {selectedTrade
            ? <TradeChart trade={selectedTrade} barsDisabled={sharedMode} />
            : <Card className="flex-1 flex items-center justify-center text-muted-foreground text-sm">
                Pick a trade from the list to chart it
              </Card>
          }
        </div>
      </div>
    </div>
  );
};

// ── Trade Table ─────────────────────────────────────────────────

interface TableProps {
  trades: StrategyTrade[];
  allTrades: StrategyTrade[]; // unsorted, original order — needed for stable index
  sort: SortState;
  setSort: (s: SortState) => void;
  selectedIdx: number;
  onSelect: (i: number) => void;
}

const COLS: { key: SortKey; label: string }[] = [
  { key: 'symbol', label: 'Sym' },
  { key: 'direction', label: 'Dir' },
  { key: 'entryTime', label: 'Entry (ET)' },
  { key: 'entryPrice', label: 'EntryPx' },
  { key: 'exitPrice', label: 'ExitPx' },
  { key: 'realizedPnL', label: 'P&L' },
  { key: 'realizedRMultiple', label: 'R' },
  { key: 'exitReason', label: 'Exit' },
  { key: 'holdingSeconds', label: 'Hold' },
];

// CSS grid template — must match header columns + row columns exactly
// Sym | Dir | Entry (ET) | EntryPx | ExitPx | P&L | R | Exit | Hold
// Total: ~510px. Sized so "02-02 10:42" (entry time) and "TakeProfit" (exit label)
// each fit on a single line at text-[11px].
const COL_TEMPLATE = '55px 30px 95px 60px 60px 60px 35px 80px 40px';

const TradeTable: React.FC<TableProps> = ({ trades, allTrades, sort, setSort, selectedIdx, onSelect }) => {
  const parentRef = useRef<HTMLDivElement | null>(null);

  // Pre-compute trade-id → original-index for O(1) lookups (was O(n) per row before)
  const idxByTradeId = useMemo(() => {
    const m = new Map<string, number>();
    allTrades.forEach((t, i) => m.set(t.id, i));
    return m;
  }, [allTrades]);

  const rowVirtualizer = useVirtualizer({
    count: trades.length,
    getScrollElement: () => parentRef.current,
    estimateSize: () => 24, // ~row height in px
    overscan: 12,
  });

  const toggleSort = (col: SortKey) => {
    setSort({
      col,
      dir: sort.col === col && sort.dir === 'asc' ? 'desc' : 'asc',
    });
  };

  return (
    <Card className="overflow-x-auto overflow-y-hidden flex flex-col max-h-[70vh] lg:max-h-none">
      {/* Header — sits outside the scroll container, rendered once */}
      <div
        className="grid border-b bg-card text-[11px] font-semibold min-w-[515px]"
        style={{ gridTemplateColumns: COL_TEMPLATE }}
      >
        {COLS.map(c => (
          <div
            key={c.key}
            onClick={() => toggleSort(c.key)}
            className="px-2 py-1.5 cursor-pointer hover:text-blue-400 select-none"
          >
            {c.label}
            {sort.col === c.key && (sort.dir === 'asc' ? ' ▲' : ' ▼')}
          </div>
        ))}
      </div>

      {/* Virtualized rows — only the visible window is rendered */}
      <div ref={parentRef} className="overflow-auto flex-1 min-w-[515px]">
        <div
          style={{
            height: `${rowVirtualizer.getTotalSize()}px`,
            width: '100%',
            position: 'relative',
          }}
        >
          {rowVirtualizer.getVirtualItems().map((vi) => {
            const t = trades[vi.index];
            const origIdx = idxByTradeId.get(t.id) ?? -1;
            const isSelected = origIdx === selectedIdx;
            const isWin = (t.realizedPnL || 0) > 0;
            return (
              <div
                key={t.id}
                onClick={() => onSelect(origIdx)}
                className={`grid border-b text-[11px] cursor-pointer whitespace-nowrap ${isSelected ? 'bg-blue-500/15' : 'hover:bg-blue-500/5'}`}
                style={{
                  gridTemplateColumns: COL_TEMPLATE,
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  height: `${vi.size}px`,
                  transform: `translateY(${vi.start}px)`,
                }}
              >
                <div className="px-2 py-1 truncate">{t.symbol}</div>
                <div className={`px-2 py-1 ${t.direction === 'Long' ? 'text-green-500' : 'text-red-500'}`}>
                  {t.direction[0]}
                </div>
                <div className="px-2 py-1">{fmtNY(t.entryTime)}</div>
                <div className="px-2 py-1 tabular-nums">{t.entryPrice.toFixed(3)}</div>
                <div className="px-2 py-1 tabular-nums">{t.exitPrice?.toFixed(3) ?? '-'}</div>
                <div className={`px-2 py-1 tabular-nums ${isWin ? 'text-green-500' : 'text-red-500'}`}>
                  {(t.realizedPnL || 0) >= 0 ? '+' : ''}{(t.realizedPnL || 0).toFixed(0)}
                </div>
                <div className="px-2 py-1 tabular-nums">
                  {t.realizedRMultiple != null ? t.realizedRMultiple.toFixed(1) : '-'}
                </div>
                <div className={`px-2 py-1 ${isWin ? 'text-green-500' : 'text-red-500'}`}>
                  {exitLabel(t)}
                </div>
                <div className="px-2 py-1">{fmtHold(t.holdingSeconds)}</div>
              </div>
            );
          })}
        </div>
      </div>
    </Card>
  );
};

// ── Trade Chart ─────────────────────────────────────────────────

// barsDisabled: the candlestick needs the authenticated /api/TimescaleDb/bars
// endpoint, which a public shared-link viewer can't reach. When set, the bar
// fetch and chart render are skipped; the trade detail + events cards (both
// pure trade data, no API) still render.
interface ChartProps { trade: StrategyTrade; barsDisabled?: boolean; }

const TradeChart: React.FC<ChartProps> = ({ trade, barsDisabled }) => {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const chartApiRef = useRef<any>(null);
  const [bars, setBars] = useState<MassiveBar[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Fetch bars whenever the selected trade changes
  useEffect(() => {
    // Shared (unauthenticated) viewer — the bars endpoint requires auth, so
    // don't even attempt the request.
    if (barsDisabled) { setBars([]); return; }
    let cancelled = false;
    const fetchBars = async () => {
      setLoading(true); setError(null);
      try {
        const entryT = new Date(trade.entryTime);
        const exitT = trade.exitTime ? new Date(trade.exitTime) : new Date(entryT.getTime() + 60 * 60 * 1000);
        // 4h before / 2h after — wide setup context, sparse-data periods just render as gaps
        const fromMs = entryT.getTime() - 4 * 60 * 60 * 1000;
        const toMs = exitT.getTime() + 2 * 60 * 60 * 1000;
        const url = `${API_BASE}/api/TimescaleDb/bars/${trade.symbol}/timescaledb?period=Minute&timeUnits=1&from=${encodeURIComponent(new Date(fromMs).toISOString())}&to=${encodeURIComponent(new Date(toMs).toISOString())}&source=massive`;
        const resp = await apiFetch(url);
        if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
        const data: MassiveBar[] = await resp.json();
        if (!cancelled) setBars(data);
      } catch (err: any) {
        if (!cancelled) { setError(err.message || String(err)); setBars([]); }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchBars();
    return () => { cancelled = true; };
  }, [trade.id, trade.symbol, trade.entryTime, trade.exitTime, barsDisabled]);

  // Render chart whenever bars or trade changes
  useEffect(() => {
    if (!containerRef.current) return;
    if (!bars.length) return;
    let cancelled = false;

    (async () => {
      const lc = await import('lightweight-charts');
      const { createChart, CandlestickSeries, createSeriesMarkers } = lc as any;
      if (cancelled || !containerRef.current) return;

      // Tear down previous chart
      if (chartApiRef.current) {
        try { chartApiRef.current.chart.remove(); } catch {}
        chartApiRef.current = null;
      }

      const isDark = document.documentElement.classList.contains('dark');
      const chart = createChart(containerRef.current, {
        autoSize: true,
        layout: { background: { color: 'transparent' }, textColor: isDark ? '#d1d4dc' : '#333' },
        grid: {
          vertLines: { color: isDark ? 'rgba(197,203,206,0.1)' : 'rgba(197,203,206,0.2)' },
          horzLines: { color: isDark ? 'rgba(197,203,206,0.1)' : 'rgba(197,203,206,0.2)' },
        },
        timeScale: {
          timeVisible: true,
          secondsVisible: false,
          borderColor: isDark ? 'rgba(197,203,206,0.3)' : '#ccc',
          tickMarkFormatter: (time: number) => {
            const d = new Date(time * 1000);
            return new Intl.DateTimeFormat('en-US', {
              timeZone: 'America/New_York',
              hour: '2-digit', minute: '2-digit', hour12: false,
            }).format(d);
          },
        },
        rightPriceScale: { borderColor: isDark ? 'rgba(197,203,206,0.3)' : '#ccc' },
        crosshair: { mode: 0 },
        localization: {
          timeFormatter: (time: number) => {
            const d = new Date(time * 1000);
            return new Intl.DateTimeFormat('en-US', {
              timeZone: 'America/New_York',
              year: 'numeric', month: '2-digit', day: '2-digit',
              hour: '2-digit', minute: '2-digit', hour12: false,
            }).format(d) + ' ET';
          },
        },
      });

      const candleSeries = chart.addSeries(CandlestickSeries, {
        upColor: '#26a69a', downColor: '#ef5350',
        borderUpColor: '#26a69a', borderDownColor: '#ef5350',
        wickUpColor: '#26a69a', wickDownColor: '#ef5350',
      });
      candleSeries.setData(bars.map(b => ({
        time: Math.floor(new Date(b.timestamp).getTime() / 1000) as any,
        open: b.open, high: b.high, low: b.low, close: b.close,
      })));

      // Markers: entry + exit
      const isLong = trade.direction === 'Long';
      const isWin = (trade.realizedPnL || 0) > 0;
      const markers: any[] = [{
        time: Math.floor(new Date(trade.entryTime).getTime() / 1000),
        position: isLong ? 'belowBar' : 'aboveBar',
        color: '#58a6ff',
        shape: isLong ? 'arrowUp' : 'arrowDown',
        text: `${isLong ? 'LONG' : 'SHORT'} @ ${trade.entryPrice.toFixed(3)}`,
      }];
      if (trade.exitTime && trade.exitPrice != null) {
        markers.push({
          time: Math.floor(new Date(trade.exitTime).getTime() / 1000),
          position: isLong ? 'aboveBar' : 'belowBar',
          color: isWin ? '#26a69a' : '#ef5350',
          shape: 'circle',
          text: `${exitLabel(trade)} @ ${trade.exitPrice.toFixed(3)} (${isWin ? '+' : ''}${(trade.realizedPnL || 0).toFixed(0)})`,
        });
      }
      createSeriesMarkers(candleSeries, markers);

      // Price lines
      candleSeries.createPriceLine({
        price: trade.initialStopPrice, color: '#ff9800', lineWidth: 1, lineStyle: 2,
        axisLabelVisible: true, title: 'init stop',
      });
      if (trade.currentStopPrice && trade.currentStopPrice !== trade.initialStopPrice) {
        candleSeries.createPriceLine({
          price: trade.currentStopPrice, color: '#ffeb3b', lineWidth: 1, lineStyle: 2,
          axisLabelVisible: true, title: 'final stop',
        });
      }
      if (trade.initialTakeProfitPrice) {
        candleSeries.createPriceLine({
          price: trade.initialTakeProfitPrice, color: '#4caf50', lineWidth: 1, lineStyle: 2,
          axisLabelVisible: true, title: 'TP',
        });
      }
      candleSeries.createPriceLine({
        price: trade.entryPrice, color: '#58a6ff', lineWidth: 1, lineStyle: 0,
        axisLabelVisible: true, title: 'entry',
      });

      chart.timeScale().fitContent();
      chartApiRef.current = { chart, candleSeries };
    })();

    return () => {
      cancelled = true;
      if (chartApiRef.current) {
        try { chartApiRef.current.chart.remove(); } catch {}
        chartApiRef.current = null;
      }
    };
  }, [bars, trade.id]);

  return (
    <>
      <Card className="p-3 text-xs flex flex-wrap items-center gap-x-5 gap-y-1">
        <div><span className="text-muted-foreground">Symbol</span> <strong>{trade.symbol}</strong></div>
        <div><span className="text-muted-foreground">Dir</span>{' '}
          <strong className={trade.direction === 'Long' ? 'text-green-500' : 'text-red-500'}>{trade.direction}</strong>
        </div>
        <div><span className="text-muted-foreground">Entry</span> <strong>{trade.entryPrice.toFixed(3)} @ {fmtNY(trade.entryTime)} ET</strong></div>
        <div><span className="text-muted-foreground">Exit</span>{' '}
          <strong className={(trade.realizedPnL || 0) >= 0 ? 'text-green-500' : 'text-red-500'}>
            {trade.exitPrice?.toFixed(3) ?? '-'} ({exitLabel(trade)})
          </strong>
          <span className="text-muted-foreground"> · raw: {trade.exitReason || '-'}</span>
        </div>
        <div><span className="text-muted-foreground">Hold</span> <strong>{fmtHold(trade.holdingSeconds)}</strong></div>
        <div><span className="text-muted-foreground">P&L</span>{' '}
          <strong className={(trade.realizedPnL || 0) >= 0 ? 'text-green-500' : 'text-red-500'}>
            {(trade.realizedPnL || 0) >= 0 ? '+' : ''}${(trade.realizedPnL || 0).toFixed(2)}
          </strong>
        </div>
        <div><span className="text-muted-foreground">R</span> <strong>{trade.realizedRMultiple?.toFixed(2) ?? '-'}</strong></div>
        <div><span className="text-muted-foreground">Qty</span> <strong>{trade.quantity}</strong></div>
        <div><span className="text-muted-foreground">MFE/MAE</span>{' '}
          <strong>{trade.maxFavorableExcursion?.toFixed(3) ?? '-'} / {trade.maxAdverseExcursion?.toFixed(3) ?? '-'}</strong>
        </div>
      </Card>

      <Card className="flex-1 min-h-[320px] lg:min-h-0 relative">
        {barsDisabled ? (
          <div className="absolute inset-0 flex items-center justify-center text-muted-foreground text-sm text-center px-4">
            Candlestick chart isn't available in the shared view —
            trade details and the event log are shown above.
          </div>
        ) : (
          <>
            {loading && (
              <div className="absolute inset-0 flex items-center justify-center text-muted-foreground text-sm z-10">
                <Loader2 className="h-4 w-4 mr-2 animate-spin" /> Loading bars…
              </div>
            )}
            {error && !loading && (
              <div className="absolute inset-0 flex items-center justify-center text-red-500 text-sm">
                Failed to fetch bars: {error}
              </div>
            )}
            {!loading && !error && bars.length === 0 && (
              <div className="absolute inset-0 flex items-center justify-center text-muted-foreground text-sm">
                No bars in range — check the trade's date is in massive_minute_bars.
              </div>
            )}
            <div ref={containerRef} className="w-full h-full min-h-[320px] lg:min-h-0" />
          </>
        )}
      </Card>

      {trade.events && trade.events.length > 0 && (
        <Card className="p-2 text-[11px] max-h-[160px] overflow-y-auto">
          <div className="grid grid-cols-[160px_130px_75px_90px_1fr] gap-2 font-semibold border-b pb-1 mb-1">
            <span>Time (ET)</span>
            <span>Type</span>
            <span>Bar Px</span>
            <span>New Stop</span>
            <span>Notes</span>
          </div>
          {trade.events.map((ev, i) => (
            <div key={i} className="grid grid-cols-[160px_130px_75px_90px_1fr] gap-2 py-0.5">
              <span>{fmtNY(ev.time, true)} ET</span>
              <span className="text-blue-400">{ev.type}</span>
              <span className="tabular-nums">{ev.price?.toFixed(3) ?? '-'}</span>
              <span className="tabular-nums text-orange-400 font-semibold">{ev.stopPrice?.toFixed(3) ?? '-'}</span>
              <span className="text-muted-foreground">{ev.notes ?? ''}</span>
            </div>
          ))}
        </Card>
      )}
    </>
  );
};

export default TradeViewerPage;
